import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DIFFICULTIES,
  TAB_OVERHANG,
  TRAY_LABEL_BAND,
  completionProof,
  createPuzzle,
  dropPiece,
  fitCompactBoard,
  gridForDifficulty,
  keyboardStep,
  movePiece,
  normalizePieceTargets,
  pieceEdges,
  placedCount,
  relayoutPuzzle,
  surfaceBounds,
  surfaceOrigin,
  trayRect,
} from "./jigsawEngine";
import { piecePath } from "./jigsawShapes";

const DIFFICULTY_LABELS = { easy: "Easy", medium: "Medium", hard: "Hard" };

let instanceCount = 0;

function availableHeight() {
  if (typeof window === "undefined") return 600;
  return Math.max(260, Math.floor(window.innerHeight * 0.78));
}

function viewportSize() {
  if (typeof window === "undefined") return { width: 1024, height: 768 };
  return { width: window.innerWidth, height: window.innerHeight };
}

/** Band (px) at the top / bottom of the viewport where a drag scrolls the page. */
const AUTO_SCROLL_EDGE = 56;
const AUTO_SCROLL_MAX_STEP = 18;

/** Nearest scrolling ancestor (the app may scroll <body> rather than the window). */
function scrollParent(el) {
  for (let node = el ? el.parentElement : null; node; node = node.parentElement) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if ((overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight) {
      return node;
    }
  }
  return document.scrollingElement || document.documentElement;
}

/**
 * Image Jigsaw Puzzle player. Pieces are interlocking SVG shapes clipping one
 * board-sized image, dragged with pointer events (mouse, touch, pen) or moved
 * with the keyboard. State lives in normalized board coordinates. Each piece
 * button is its cell enlarged by the tab overhang; only the piece outline
 * takes pointer input, so the transparent overhang never blocks a neighbour.
 * onRoundStart fires for every new puzzle; onComplete receives the
 * completion proof; completionSlot renders inside the success panel.
 * Every piece gets its own tray slot; the tray spans the play area width
 * (centred under the board) and grows taller as needed, so the empty surface
 * still scrolls the page and dragging near the viewport edge scrolls it.
 */
function JigsawPuzzle({
  imageUrl,
  imageWidth,
  imageHeight,
  pieces,
  defaultDifficulty,
  title,
  onRoundStart,
  onComplete,
  completionSlot,
}) {
  const targets = useMemo(() => normalizePieceTargets(pieces), [pieces]);
  const [difficulty, setDifficulty] = useState(
    DIFFICULTIES.indexOf(defaultDifficulty) >= 0 ? defaultDifficulty : "easy"
  );
  const [image, setImage] = useState(null);
  const [imageStatus, setImageStatus] = useState("loading");
  const [reloadKey, setReloadKey] = useState(0);
  const [areaWidth, setAreaWidth] = useState(0);
  const [areaHeight, setAreaHeight] = useState(availableHeight());
  const [viewport, setViewport] = useState(viewportSize());
  const [puzzle, setPuzzle] = useState(null);
  const [round, setRound] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const [announcement, setAnnouncement] = useState("");

  const containerRef = useRef(null);
  const surfaceRef = useRef(null);
  const dragRef = useRef(null);
  const autoScrollRef = useRef(null);
  const puzzleRef = useRef(null);
  const playAgainRef = useRef(null);
  const idPrefixRef = useRef(null);
  if (idPrefixRef.current === null) {
    instanceCount += 1;
    idPrefixRef.current = "jigsaw-" + instanceCount + "-piece-";
  }
  puzzleRef.current = puzzle;

  const stopAutoScroll = () => {
    const auto = autoScrollRef.current;
    autoScrollRef.current = null;
    if (auto && auto.frame !== null) window.cancelAnimationFrame(auto.frame);
  };

  // Load the image once per URL to learn its real dimensions.
  useEffect(() => {
    let cancelled = false;
    setImageStatus("loading");
    setImage(null);
    if (!imageUrl) {
      setImageStatus("error");
      return undefined;
    }
    const img = new window.Image();
    img.onload = () => {
      if (cancelled) return;
      const width = img.naturalWidth || imageWidth;
      const height = img.naturalHeight || imageHeight;
      if (!width || !height) {
        setImageStatus("error");
        return;
      }
      setImage({ width, height });
      setImageStatus("ready");
    };
    img.onerror = () => {
      if (!cancelled) setImageStatus("error");
    };
    img.src = imageUrl;
    return () => {
      cancelled = true;
      img.onload = null;
      img.onerror = null;
    };
  }, [imageUrl, imageWidth, imageHeight, reloadKey]);

  // Track the available play area (resize / orientation change).
  useEffect(() => {
    let frame = null;
    const measure = () => {
      frame = null;
      const el = containerRef.current;
      if (!el) return;
      // On phones the column shrinks to fit its content, so the surface's
      // current width would hold the area at its old size after a rotation.
      const surface = surfaceRef.current;
      const surfaceWidth = surface ? surface.style.width : "";
      if (surface) surface.style.width = "0px";
      setAreaWidth(el.clientWidth);
      if (surface) surface.style.width = surfaceWidth;
      setAreaHeight(availableHeight());
      const next = viewportSize();
      setViewport((prev) =>
        prev.width === next.width && prev.height === next.height ? prev : next
      );
    };
    const schedule = () => {
      if (frame === null) frame = window.requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener("resize", schedule);
    window.addEventListener("orientationchange", schedule);
    return () => {
      window.removeEventListener("resize", schedule);
      window.removeEventListener("orientationchange", schedule);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [imageStatus]);

  const board = useMemo(() => {
    if (!image || areaWidth <= 0) return null;
    return fitCompactBoard(image, areaWidth, areaHeight, viewport.width, viewport.height, targets, difficulty);
  }, [image, areaWidth, areaHeight, viewport, targets, difficulty]);

  const boardReady = !!board;
  const layout = board ? board.layout : "stacked";
  const trayWidth = board && layout === "stacked" ? Math.max(1, areaWidth / board.width) : 1;

  const gridFor = useCallback(
    (level) => (image && board ? gridForDifficulty(image, board, targets, level) : null),
    [image, board, targets]
  );

  // New puzzle on first load, difficulty change or restart — not on resize.
  useEffect(() => {
    if (!boardReady) return;
    const grid = gridFor(difficulty);
    if (!grid) return;
    setPuzzle(createPuzzle(grid.rows, grid.cols, undefined, layout, board.width / board.height, true, trayWidth));
    setSelectedId(null);
    setDraggingId(null);
    dragRef.current = null;
    setAnnouncement(
      `New ${DIFFICULTY_LABELS[difficulty]} puzzle with ${grid.pieces} pieces.`
    );
    if (onRoundStart) onRoundStart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardReady, difficulty, round, image]);

  // Tray moved beside / below the board (rotation) or resized with the area: same puzzle, new layout.
  useEffect(() => {
    dragRef.current = null;
    setDraggingId(null);
    setPuzzle((s) => relayoutPuzzle(s, layout, true, trayWidth));
  }, [layout, trayWidth]);

  useEffect(() => () => stopAutoScroll(), []);

  useEffect(() => {
    if (puzzle && puzzle.completed && playAgainRef.current) {
      playAgainRef.current.focus();
    }
  }, [puzzle && puzzle.completed]);

  const pieceW = board && puzzle ? board.width / puzzle.cols : 0;
  const pieceH = board && puzzle ? board.height / puzzle.rows : 0;
  const inset = TAB_OVERHANG * Math.min(pieceW, pieceH);
  const edges = puzzle ? puzzle.edges : null;
  // Shapes depend only on the shared edges and piece size, not on positions.
  const piecePaths = useMemo(() => {
    if (!puzzle || pieceW <= 0 || pieceH <= 0) return [];
    return puzzle.pieces.map((p) => piecePath(pieceEdges(puzzle, p), pieceW, pieceH, inset));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edges, pieceW, pieceH]);

  const toNormalized = (clientX, clientY) => {
    const rect = surfaceRef.current.getBoundingClientRect();
    const origin = surfaceOrigin({ trayWidth }, layout);
    return {
      x: (clientX - rect.left) / board.width + origin.x,
      y: (clientY - rect.top) / board.height + origin.y,
    };
  };

  // The play surface can be taller than the screen, so holding a dragged
  // piece near the top / bottom edge scrolls the page under it.
  const autoScrollStep = (clientY) => {
    const h = window.innerHeight;
    if (clientY < AUTO_SCROLL_EDGE) return -Math.ceil(((AUTO_SCROLL_EDGE - clientY) / AUTO_SCROLL_EDGE) * AUTO_SCROLL_MAX_STEP);
    if (clientY > h - AUTO_SCROLL_EDGE) return Math.ceil(((clientY - (h - AUTO_SCROLL_EDGE)) / AUTO_SCROLL_EDGE) * AUTO_SCROLL_MAX_STEP);
    return 0;
  };

  const updateAutoScroll = (clientX, clientY) => {
    if (autoScrollStep(clientY) === 0) {
      stopAutoScroll();
      return;
    }
    if (autoScrollRef.current) {
      autoScrollRef.current.clientX = clientX;
      autoScrollRef.current.clientY = clientY;
      return;
    }
    const auto = { clientX, clientY, frame: null, scroller: scrollParent(surfaceRef.current) };
    const tick = () => {
      const drag = dragRef.current;
      if (autoScrollRef.current !== auto || !drag || !surfaceRef.current) {
        if (autoScrollRef.current === auto) autoScrollRef.current = null;
        return;
      }
      const step = autoScrollStep(auto.clientY);
      const before = auto.scroller.scrollTop;
      if (step !== 0) auto.scroller.scrollTop = before + step;
      if (auto.scroller.scrollTop !== before) {
        const point = toNormalized(auto.clientX, auto.clientY);
        setPuzzle((s) => movePiece(s, drag.id, point.x - drag.offsetX, point.y - drag.offsetY));
      }
      auto.frame = window.requestAnimationFrame(tick);
    };
    autoScrollRef.current = auto;
    auto.frame = window.requestAnimationFrame(tick);
  };

  const finishDrop = (id, x, y) => {
    const current = puzzleRef.current;
    if (!current) return;
    const result = dropPiece(current, id, x, y);
    setPuzzle(result.state);
    if (result.state.completed) {
      setSelectedId(null);
      setAnnouncement(`Puzzle complete in ${result.state.moves} moves!`);
      if (onComplete && !current.completed) onComplete(completionProof(result.state, difficulty));
    } else if (result.snapped) {
      setSelectedId(null);
      setAnnouncement(
        `Piece placed. ${placedCount(result.state)} of ${result.state.pieces.length} pieces in place.`
      );
    }
  };

  const handlePointerDown = (event, piece) => {
    if (!board || piece.placed || puzzle.completed) return;
    if (event.button !== undefined && event.button !== 0 && event.pointerType === "mouse") return;
    event.preventDefault();
    // The button itself has pointer-events: none; capture on the hit outline.
    const captureTarget =
      event.target && event.target.setPointerCapture ? event.target : event.currentTarget;
    if (captureTarget.setPointerCapture) {
      try {
        captureTarget.setPointerCapture(event.pointerId);
      } catch (e) {
        // Capture is best effort; move/up still arrive while over the piece.
      }
    }
    const point = toNormalized(event.clientX, event.clientY);
    dragRef.current = {
      id: piece.id,
      pointerId: event.pointerId,
      offsetX: point.x - piece.x,
      offsetY: point.y - piece.y,
    };
    setSelectedId(piece.id);
    setDraggingId(piece.id);
    setPuzzle((s) => movePiece(s, piece.id, piece.x, piece.y));
  };

  const handlePointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId || !board) return;
    event.preventDefault();
    const point = toNormalized(event.clientX, event.clientY);
    setPuzzle((s) => movePiece(s, drag.id, point.x - drag.offsetX, point.y - drag.offsetY));
    updateAutoScroll(event.clientX, event.clientY);
  };

  const handlePointerUp = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    stopAutoScroll();
    dragRef.current = null;
    setDraggingId(null);
    const point = toNormalized(event.clientX, event.clientY);
    finishDrop(drag.id, point.x - drag.offsetX, point.y - drag.offsetY);
  };

  const handlePointerCancel = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    stopAutoScroll();
    dragRef.current = null;
    setDraggingId(null);
  };

  const handleKeyDown = (event, piece) => {
    if (!puzzle || piece.placed || puzzle.completed) return;
    const key = event.key;
    if (key === "Enter" || key === " " || key === "Spacebar") {
      event.preventDefault();
      if (selectedId !== piece.id) {
        setSelectedId(piece.id);
        setPuzzle((s) => movePiece(s, piece.id, piece.x, piece.y));
        setAnnouncement(
          `Piece selected. Use arrow keys to move it, Enter to drop, Escape to cancel.`
        );
      } else {
        finishDrop(piece.id, piece.x, piece.y);
        if (!puzzleRef.current.pieces[piece.id].placed) {
          setAnnouncement("Piece dropped. Not in its place yet.");
        }
      }
      return;
    }
    if (key === "Escape") {
      setSelectedId(null);
      return;
    }
    const arrows = {
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
      ArrowUp: [0, -1],
      ArrowDown: [0, 1],
    };
    if (arrows[key] && selectedId === piece.id) {
      event.preventDefault();
      const step = keyboardStep(puzzle);
      const factor = event.shiftKey ? 4 : 1;
      const nx = piece.x + arrows[key][0] * step.dx * factor;
      const ny = piece.y + arrows[key][1] * step.dy * factor;
      setPuzzle((s) => movePiece(s, piece.id, nx, ny));
    }
  };

  const restart = () => setRound((r) => r + 1);

  if (imageStatus === "error") {
    return (
      <div className="games-panel games-panel--error" role="alert">
        <h2 className="games-panel__title">This puzzle image could not be loaded</h2>
        <p className="games-panel__body">Check your connection and try again.</p>
        <button
          type="button"
          className="btn btn-danger"
          onClick={() => setReloadKey((k) => k + 1)}
        >
          Try again
        </button>
      </div>
    );
  }

  const currentGrid = gridFor(difficulty);
  const placed = puzzle ? placedCount(puzzle) : 0;
  const total = puzzle ? puzzle.pieces.length : 0;
  const shown = puzzle ? relayoutPuzzle(puzzle, layout, true, trayWidth) : null;
  let surfaceStyle = null;
  let trayStyle = null;
  let trayHeaderStyle = null;
  let offsetX = 0;
  if (board && shown) {
    const bounds = surfaceBounds(layout, shown);
    const origin = surfaceOrigin(shown, layout);
    offsetX = -origin.x * board.width;
    const surfaceWidth = Math.max(board.surfaceWidth, Math.ceil(board.width * bounds.width - 1e-6));
    const surfaceHeight = Math.max(board.surfaceHeight, Math.ceil(board.height * bounds.height - 1e-6));
    const tray = trayRect(shown, layout);
    const trayLeft = Math.round((tray.x - origin.x) * board.width);
    const trayTop = Math.round(tray.y * board.height);
    surfaceStyle = { width: surfaceWidth, height: surfaceHeight };
    trayStyle = {
      left: trayLeft,
      top: trayTop,
      width: Math.min(surfaceWidth, Math.ceil((tray.x - origin.x + tray.width) * board.width - 1e-6)) - trayLeft,
      height: Math.min(surfaceHeight, Math.ceil((tray.y + tray.height) * board.height - 1e-6)) - trayTop,
    };
    // Ends where loose pieces start; the header sits below the tray's 1px top border.
    trayHeaderStyle = { height: Math.max(0, Math.floor((tray.y + TRAY_LABEL_BAND) * board.height) - trayTop - 1) };
  }
  const boxW = pieceW + 2 * inset;
  const boxH = pieceH + 2 * inset;
  const cssUrl = 'url("' + String(imageUrl).replace(/\\/g, "\\\\").replace(/"/g, '\\"') + '")';

  return (
    <div className="jigsaw" ref={containerRef}>
      <div className="jigsaw__controls" role="group" aria-label="Puzzle controls">
        <label className="jigsaw__control">
          <span className="jigsaw__control-label">Difficulty</span>
          <select
            className="jigsaw__select"
            value={difficulty}
            onChange={(e) => setDifficulty(e.target.value)}
            disabled={!boardReady}
            aria-describedby="jigsaw-difficulty-note"
          >
            {DIFFICULTIES.map((level) => {
              const grid = gridFor(level);
              return (
                <option key={level} value={level}>
                  {DIFFICULTY_LABELS[level]}
                  {grid ? ` · ${grid.pieces} pieces` : ""}
                </option>
              );
            })}
          </select>
        </label>
        <button
          type="button"
          className="btn btn-outline-secondary jigsaw__button"
          onClick={restart}
          disabled={!boardReady}
        >
          Restart
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary jigsaw__button"
          aria-pressed={showHint}
          onClick={() => setShowHint((v) => !v)}
          disabled={!boardReady}
        >
          {showHint ? "Hide picture hint" : "Show picture hint"}
        </button>
        <span className="jigsaw__progress" aria-hidden="true">
          {puzzle ? `${placed} / ${total} placed` : ""}
        </span>
      </div>
      <p id="jigsaw-difficulty-note" className="jigsaw__note">
        Changing difficulty starts a new puzzle
        {currentGrid ? ` (${currentGrid.cols} × ${currentGrid.rows} grid)` : ""}. Drag pieces
        onto the picture area, or focus a piece and press Enter, move it with the arrow keys
        (Shift for bigger steps) and press Enter to drop it.
      </p>

      <div className="jigsaw__sr-status" role="status" aria-live="polite">
        {announcement}
      </div>

      {!board || !puzzle ? (
        <div className="games-panel" role="status" aria-live="polite">
          <p className="games-panel__body">Loading puzzle…</p>
        </div>
      ) : (
        <div
          className="jigsaw__surface"
          ref={surfaceRef}
          style={surfaceStyle}
          data-layout={layout}
          aria-label={`${title || "Puzzle"} play area`}
        >
          <div
            className={"jigsaw__board" + (showHint ? " has-hint" : "")}
            style={{
              left: offsetX,
              width: board.width,
              height: board.height,
              backgroundImage: showHint ? cssUrl : "none",
              backgroundSize: `${board.width}px ${board.height}px`,
              "--jigsaw-cols": puzzle.cols,
              "--jigsaw-rows": puzzle.rows,
            }}
            aria-hidden="true"
          />
          <div
            className={"jigsaw__tray jigsaw__tray--" + layout}
            style={trayStyle}
            aria-hidden="true"
          >
            <div className="jigsaw__tray-header" style={trayHeaderStyle}>
              <span className="jigsaw__tray-label">Pieces</span>
            </div>
          </div>
          {shown.pieces.map((piece) => {
            const isSelected = selectedId === piece.id;
            const clipId = idPrefixRef.current + piece.id;
            const outline = piecePaths[piece.id] || "";
            return (
              <button
                key={piece.id}
                type="button"
                className={
                  "jigsaw__piece" +
                  (piece.placed ? " is-placed" : "") +
                  (isSelected ? " is-selected" : "") +
                  (draggingId === piece.id ? " is-dragging" : "")
                }
                data-piece-id={piece.id}
                data-placed={piece.placed ? "true" : "false"}
                style={{
                  left: offsetX + piece.x * board.width - inset,
                  top: piece.y * board.height - inset,
                  width: boxW,
                  height: boxH,
                  zIndex: piece.placed ? 1 : 10 + piece.z,
                }}
                aria-label={
                  piece.placed
                    ? `Piece row ${piece.row + 1}, column ${piece.col + 1}, placed`
                    : `Piece row ${piece.row + 1}, column ${piece.col + 1}`
                }
                aria-pressed={piece.placed ? undefined : isSelected}
                aria-disabled={piece.placed || puzzle.completed ? true : undefined}
                tabIndex={piece.placed || puzzle.completed ? -1 : 0}
                onPointerDown={(e) => handlePointerDown(e, piece)}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerCancel}
                onKeyDown={(e) => handleKeyDown(e, piece)}
              >
                <svg
                  className="jigsaw__piece-svg"
                  width={boxW}
                  height={boxH}
                  viewBox={`0 0 ${boxW} ${boxH}`}
                  aria-hidden="true"
                  focusable="false"
                >
                  <defs>
                    <clipPath id={clipId}>
                      <path d={outline} />
                    </clipPath>
                  </defs>
                  <image
                    xlinkHref={imageUrl}
                    x={inset - piece.col * pieceW}
                    y={inset - piece.row * pieceH}
                    width={board.width}
                    height={board.height}
                    preserveAspectRatio="none"
                    clipPath={`url(#${clipId})`}
                  />
                  <path className="jigsaw__piece-outline" d={outline} />
                </svg>
              </button>
            );
          })}
        </div>
      )}

      {puzzle && puzzle.completed ? (
        <div className="games-panel games-panel--success" role="alert">
          <h2 className="games-panel__title">Puzzle complete!</h2>
          <p className="games-panel__body">
            You placed all {total} pieces in {puzzle.moves} moves.
          </p>
          <button
            type="button"
            className="btn btn-danger"
            onClick={restart}
            ref={playAgainRef}
          >
            Play again
          </button>
          {completionSlot || null}
        </div>
      ) : null}
    </div>
  );
}

export default JigsawPuzzle;
